// --------------------------------------------------
// easing.js v0.5.4
// Generic set of easing functions with AMD support
// https://github.com/danro/easing-js
// This code may be freely distributed under the MIT license
// http://danro.mit-license.org/
// --------------------------------------------------
// All functions adapted from Thomas Fuchs & Jeremy Kahn
// Easing Equations (c) 2003 Robert Penner, BSD license
// https://raw.github.com/danro/easing-js/master/LICENSE
// --------------------------------------------------

// --------------------------------------------------
// Quick read to catch-up on Tweening functions:
// http://robertpenner.com/easing/penner_chapter7_tweening.pdf
// --------------------------------------------------

// --------------------------------------------------
// Illustrated Easing functions:
// 1) https://easings.net/
// 2) http://easing.webflow.io/
// --------------------------------------------------

/**
 * BOUNCE
 *
 * @param {number} pos
 * @returns {number}
 */
const bounce = function( pos ) {
  if (pos < (1/2.75)) {
    return (7.5625*pos*pos);
  } else if (pos < (2/2.75)) {
    return (7.5625*(pos-=(1.5/2.75))*pos + 0.75);
  } else if (pos < (2.5/2.75)) {
    return (7.5625*(pos-=(2.25/2.75))*pos + 0.9375);
  } else {
    return (7.5625*(pos-=(2.625/2.75))*pos + 0.984375);
  }
};

/**
 * BOUNCE past
 *
 * @param {number} pos
 * @returns {number}
 */
const bouncePast = function( pos ) {
  if (pos < (1/2.75)) {
    return (7.5625*pos*pos);
  } else if (pos < (2/2.75)) {
    return 2 - (7.5625*(pos-=(1.5/2.75))*pos + 0.75);
  } else if (pos < (2.5/2.75)) {
    return 2 - (7.5625*(pos-=(2.25/2.75))*pos + 0.9375);
  } else {
    return 2 - (7.5625*(pos-=(2.625/2.75))*pos + 0.984375);
  }
};

/**
 * Elastic
 *
 * @param {number} pos
 * @returns {number}
 */
const elastic = function( pos ) {
  return -1 * Math.pow(4,-8*pos) * Math.sin((pos*6-1)*(2*Math.PI)/2) + 1;
};

/**
 * Ease IN Quadratic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInQuad = function( pos ) {
  return Math.pow( pos, 2);
};

/**
 * Ease OUT Quadratic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutQuad = function( pos ) {
  return -(Math.pow((pos-1), 2) -1);
};

/**
 * Ease IN-OUT Quadratic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutQuad = function( pos ) {
  if ((pos/=0.5) < 1)
    return 0.5 * Math.pow( pos, 2);
  return -0.5 * ((pos-=2)*pos - 2);
};

/**
 * Ease IN Cubic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInCubic = function( pos ) {
  return Math.pow( pos, 3);
};

/**
 * Ease Out Cubic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutCubic = function( pos ) {
  return (Math.pow((pos-1), 3) +1);
};

/**
 * Ease IN-OUT Cubic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutCubic = function( pos ) {
  if ((pos/=0.5) < 1)
    return 0.5*Math.pow( pos, 3);
  return 0.5 * (Math.pow((pos-2),3) + 2);
};

/**
 * Ease IN Quartic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInQuart = function( pos ) {
  return Math.pow( pos, 4);
};

/**
 * Ease OUT Quartic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutQuart = function( pos ) {
  return -(Math.pow((pos-1), 4) -1);
};

/**
 * Ease IN-OUT Quartic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutQuart = function( pos ) {
  if ((pos/=0.5) < 1)
    return 0.5 * Math.pow( pos, 4);
  return -0.5 * ((pos-=2)*Math.pow( pos, 3) - 2);
};

/**
 * Ease IN Quintic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInQuint = function( pos ) {
  return Math.pow( pos, 5);
};

/**
 * Ease OUT Quintic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutQuint = function( pos ) {
  return (Math.pow((pos-1), 5) +1);
};

/**
 * Ease IN-OUT Quintic
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutQuint = function( pos ) {
  if ((pos/=0.5) < 1)
    return 0.5*Math.pow( pos, 5);
  return 0.5 * (Math.pow((pos-2),5) + 2);
};

/**
 * Ease IN Sinusoidal
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInSine = function( pos ) {
  return -Math.cos(pos * (Math.PI/2)) + 1;
};

/**
 * Ease OUT Sinusoidal
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutSine = function( pos ) {
  return Math.sin(pos * (Math.PI/2));
};

/**
 * Ease IN-OUT Sinusoidal
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutSine = function( pos ) {
  return (-0.5 * (Math.cos(Math.PI*pos) -1));
};

/**
 * Ease IN Exponential
 *
 * @param {number} pos
 * @returns {number}      2^( 10 * (pos - 1) )
 */
const easeInExpo = function( pos ) {
  return ( pos === 0 ) ? 0 : Math.pow( 2, 10 * (pos - 1) );
};

/**
 * Ease OUT Exponential
 *
 * @param {number} pos
 * @returns {number}      ( -1 * 2^( -10 * pos ) ) + 1
 */
const easeOutExpo = function( pos ) {
  return ( pos === 1 ) ? 1 : -Math.pow(2, -10 * pos) + 1;
};

/**
 * Ease IN-OUT Exponential
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutExpo = function( pos ) {
  if( pos === 0 ) return 0;
  if( pos === 1 ) return 1;
  if((pos/=0.5) < 1)
    return 0.5 * Math.pow(2,10 * (pos-1));
  return 0.5 * (-Math.pow(2, -10 * --pos) + 2);
};

/**
 * Ease IN Circular
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInCirc = function( pos ) {
  return -(Math.sqrt(1 - (pos*pos)) - 1);
};

/**
 * Ease OUT Circular
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutCirc = function( pos ) {
  return Math.sqrt(1 - Math.pow((pos-1), 2));
};

/**
 * Ease IN-OUT Circular
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutCirc = function( pos ) {
  if((pos/=0.5) < 1)
    return -0.5 * (Math.sqrt(1 - pos*pos) - 1);
  return 0.5 * (Math.sqrt(1 - (pos-=2)*pos) + 1);
};

/**
 * Ease OUT Bounce
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutBounce = function( pos ) {
  if (( pos ) < (1/2.75)) {
    return (7.5625*pos*pos);
  } else if (pos < (2/2.75)) {
    return (7.5625*(pos-=(1.5/2.75))*pos + 0.75);
  } else if (pos < (2.5/2.75)) {
    return (7.5625*(pos-=(2.25/2.75))*pos + 0.9375);
  } else {
    return (7.5625*(pos-=(2.625/2.75))*pos + 0.984375);
  }
};

/**
 * Ease IN Back
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInBack = function( pos ) {
  const s = 1.70158;
  return ( pos ) * pos * ( ( s + 1 ) * pos - s );
};

/**
 * Ease OUT Back
 *
 * @param {number} pos
 * @returns {number}
 */
const easeOutBack = function( pos ) {
  const s = 1.70158;
  return (pos=pos-1)*pos*((s+1)*pos + s) + 1;
};

/**
 * Ease IN-OUT Back
 *
 * @param {number} pos
 * @returns {number}
 */
const easeInOutBack = function( pos ) {
  let s = 1.70158;
  if((pos/=0.5) < 1)
    return 0.5*(pos*pos*(((s*=(1.525))+1)*pos -s));
  return 0.5*((pos-=2)*pos*(((s*=(1.525))+1)*pos +s) +2);
};

/**
 * Ease FROM
 *
 * @param {number} pos
 * @returns {number}
 */
const easeFrom = function( pos ) {
  return Math.pow( pos, 4 );
};

/**
 * Ease TO
 *
 * @param {number} pos
 * @returns {number}
 */
const easeTo = function( pos ) {
  return Math.pow( pos, 0.25 );
};

/**
 * Ease FROM-TO
 *
 * @param {number} pos
 * @returns {number}
 */
const easeFromTo = function( pos ) {
  if ((pos/=0.5) < 1)
    return 0.5*Math.pow( pos, 4);
  return -0.5 * ((pos-=2)*Math.pow( pos, 3) - 2);
};

/**
 * Swing FROM
 *
 * @param {number} pos
 * @returns {number}
 */
const swingFrom = function( pos ) {
  const s = 1.70158;
  return pos*pos*((s+1)*pos - s);
};

/**
 * Swing TO
 *
 * @param {number} pos
 * @returns {number}
 */
const swingTo = function( pos ) {
  const s = 1.70158;
  return (pos-=1)*pos*((s+1)*pos + s) + 1;
};

/**
 * Swing FROM-TO
 *
 * @param {number} pos
 * @returns {number}
 */
const swingFromTo = function( pos ) {
  let s = 1.70158;
  return ((pos/=0.5) < 1) ? 0.5*(pos*pos*(((s*=(1.525))+1)*pos - s)) :
    0.5*((pos-=2)*pos*(((s*=(1.525))+1)*pos + s) + 2);
};

const EasingEquations = {
  // bounce,
  // bouncePast,
  // elastic
  // easeInQuad,
  // easeOutQuad,
  // easeInOutQuad,
  // easeInCubic,
  // easeOutCubic,
  // easeInOutCubic,
  // easeInQuart,
  // easeOutQuart,
  // easeInOutQuart,
  // easeInQuint,
  // easeOutQuint,
  // easeInOutQuint,
  // easeInSine,
  easeOutSine,
  easeInOutSine,
  // easeInExpo,
  // easeOutExpo,
  // easeInOutExpo,
  // easeInCirc,
  // easeOutCirc,
  // easeInOutCirc,
  // easeOutBounce,
  // easeInBack,
  // easeOutBack,
  // easeInOutBack,
  // easeFrom,
  // easeTo,
  // easeFromTo,
  // swingFrom,
  // swingTo,
  // swingFromTo,
};

export { EasingEquations };
